import { I18n } from "@assets/strings";
import React, { JSX, useEffect, useState } from "react";
import styled from "styled-components";
import emptyExpImage from "@assets/images/empty_exp.png";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { ExperienceType } from "@domain/experience/experience";
import { useUserExperiences } from "@domain/experience/hooks";
import { useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Secondary } from "@ui/components/containers/textBlocks";
import { ExperienceEditionModal } from "@ui/components/experience/experienceEditionModal";
import { ExperienceView } from "@ui/components/experience/experienceView";
import { ChevronDownIcon } from "@ui/components/icons";
import { row, Spacer } from "@ui/components/layout";
import { CardSkeleton } from "@ui/components/skeleton/containerSkeletons";
import { useModalTools } from "@ui/modals/modalContext";
import { gap, mobile } from "@ui/styles/utils";

interface ProfileExperiencesProps extends React.HTMLAttributes<HTMLDivElement> {
	profileId: string;
	type: ExperienceType;
}

export const ProfileExperiences: React.FC<ProfileExperiencesProps> = ({ profileId, type, ...htmlProps }) => {
	const { result: experiences, totalElements } = useUserExperiences(profileId, type);
	const isMe = useCurrentProfile()?.id === profileId;
	const { createModal } = useModalTools();
	const isAcademic = type === ExperienceType.ACADEMIC;
	const isEmpty = totalElements !== undefined && totalElements < 1;
	const isMobile = useIsMobile();
	const [opened, setOpened] = useState(isMobile && isEmpty);

	useEffect(() => {
		setOpened(!isMobile || isEmpty);
	}, [profileId, isMobile, isEmpty]);

	const renderTitle = (): JSX.Element => (
		<Title onClick={() => isMobile && setOpened((o) => !o)}>
			<PrimaryMedium>
				{isAcademic ? I18n.t("profile.academic_experiences.label") : I18n.t("profile.professional_experiences.label")}
			</PrimaryMedium>
			{isMe && !isEmpty && (
				<RoundedPlusButton
					onClick={(e) => {
						e.stopPropagation();
						createModal((close) => <ExperienceEditionModal type={type} close={close} />);
					}}
				/>
			)}
			<Spacer />
			{isMobile && (
				<div>
					<StyledChevronDownIcon reversed={opened} />
				</div>
			)}
		</Title>
	);

	const renderCreateButton = (): JSX.Element => (
		<CreateButton onClick={() => createModal((close) => <ExperienceEditionModal type={type} close={close} />)}>
			{I18n.t("profile.experiences.new")}
		</CreateButton>
	);

	if (isMobile && !isMe && isEmpty) {
		return null;
	}

	return (
		<Container {...htmlProps}>
			{!isMobile && renderTitle()}
			{!isMobile && isEmpty && isMe ? (
				<EmptyContent>
					<img src={emptyExpImage} />
					{renderCreateButton()}
				</EmptyContent>
			) : (
				<Experiences>
					{isEmpty ? (
						isMe ? (
							<div>
								{isMobile && renderTitle()}
								{opened && renderCreateButton()}
							</div>
						) : (
							!isMobile && <Secondary>{I18n.t("profile.experiences.empty")}</Secondary>
						)
					) : (
						<>
							{isMobile && renderTitle()}
							{opened &&
								experiences.map((exp) =>
									exp && exp.id ? <ExperienceView key={exp.id} profileId={profileId} experience={exp} /> : undefined
								)}
						</>
					)}
				</Experiences>
			)}
		</Container>
	);
};

export const ProfileExperiencesSkeleton: React.FC<Omit<ProfileExperiencesProps, "profileId">> = ({
	type,
	...htmlProps
}) => {
	return (
		<Container {...htmlProps}>
			<PrimaryMedium>
				{type === "ACADEMIC"
					? I18n.t("profile.academic_experiences.label")
					: I18n.t("profile.professional_experiences.label")}
			</PrimaryMedium>
			<SkeletonContainer>
				<div>
					<CardSkeleton />
					<CardSkeleton />
				</div>
				<div />
			</SkeletonContainer>
		</Container>
	);
};

const Container = styled.div`
	${gap(10)};
	word-break: break-word;
`;

const SkeletonContainer = styled(RoundedBlock)`
	background-color: transparent;
	padding: 16px 70px 0;
	position: relative;
	overflow: hidden;
	*  {
		animation: none;
	}

	> div {
		${gap(10)};
	}

	> :last-child {
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0) 60%, #ffffff);
	}
`;

const Title = styled.div`
	${row(6, "flex-start", "center")};

	> svg {
		display: block;
		cursor: pointer;
		margin-bottom: 2px;

		path {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}
`;

const Experiences = styled(RoundedBlock)`
	> * {
		padding: 20px 16px;
		&:not(:last-child) {
			border-bottom: 1px solid ${({ theme }) => theme.colors.lightergray};
		}
	}
`;

const EmptyContent = styled(RoundedBlock)`
	overflow: hidden;
	> img {
		width: 101%;
		height: 140px;
	}
`;

const CreateButton = styled(PrimaryButton)`
	margin: 0px auto 23px;
	padding: 6px 14px;
	font-size: 0.8125rem;

	@media ${mobile} {
		margin: 23px 0;
	}
`;

const StyledChevronDownIcon = styled(ChevronDownIcon)`
	margin-right: 3px;
`;
